// Record a publication: the piece, and the papers it ran in.
//
// A publication IS its list of outlets, and each outlet holds the screenshot of how
// the piece appeared there. Nothing else — there is no publication-level file, so
// there is one file section, inside the outlet cards.
//
// Files belong to a saved publication (the object key is built from its id), so a
// NEW publication is saved first and the dialog then stays open with the file slots
// enabled. Making the user reopen the row to attach the screenshots would be silly
// when attaching them is the point of the exercise.
import { useState } from 'react';
import { publicationsApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import { TextInput, DateInput, Textarea } from './Form';
import { ErrorText } from './Loader';
import OutletSection from './OutletSection';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function PublicationModal({ jobId, publication, onClose, onSaved }) {
  // `saved` is the persisted row: the one passed in when editing, or the one this
  // dialog just created. Its presence is what unlocks the file slots.
  const [saved, setSaved] = useState(publication && publication.id ? publication : null);
  const [form, setForm] = useState({
    title: (publication && publication.title) || '',
    published_date: (publication && publication.published_date) || todayIso(),
    notes: (publication && publication.notes) || '',
  });
  // The outlet set is edited as a whole and sent as a whole; the server diffs it
  // against what is stored rather than clearing and re-inserting, so clippings
  // survive an edit.
  const [outlets, setOutlets] = useState(
    ((publication && publication.outlets) || []).map((o) => ({
      outlet_code: o.outlet_code, ran_on: o.ran_on || null, url: o.url || null,
    }))
  );
  const [files, setFiles] = useState((publication && publication.files) || []);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Anything persisted while this dialog was open — the record itself or a file.
  // Dismissing after a file was attached still has to refresh the list behind it,
  // which a plain onClose would not do.
  const [dirty, setDirty] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const close = () => (dirty ? onSaved() : onClose());

  const submit = async () => {
    setError(null);
    try {
      if (saved) {
        await publicationsApi.update(saved.id, { ...form, outlets });
        onSaved();
      } else {
        // Stay open on the fresh record so files can be attached right away.
        const created = await publicationsApi.create({ ...form, outlets, job_id: jobId });
        setSaved(created);
        setFiles(created.files || []);
        setDirty(true);
      }
    } catch (e) {
      setError(e);
    }
  };

  // Every upload belongs to an outlet — the screenshot of how the piece appeared in
  // that paper. Rejections propagate so the caller can show a per-file status (the
  // bulk strip) or a section-level message.
  const attach = async (file, outletCode) => {
    if (!saved) return null;
    setError(null);
    setBusy(true);
    try {
      const created = await publicationsApi.addFile(saved.id, file, outletCode);
      setFiles((current) => [...current, created]);
      setDirty(true);
      return created;
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (file) => {
    setError(null);
    try {
      await publicationsApi.removeFile(file.id);
      setFiles((current) => current.filter((f) => f.id !== file.id));
      setDirty(true);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={saved ? he.publications.edit : he.publications.add}
      onClose={close}
      onSave={submit}
      saveDisabled={busy}
    >
      <TextInput
        label={he.publications.name}
        value={form.title}
        onChange={set('title')}
        placeholder={he.publications.namePlaceholder}
      />
      <DateInput label={he.publications.date} value={form.published_date} onChange={set('published_date')} />
      <Textarea label={he.publications.notes} value={form.notes} onChange={set('notes')} />

      <OutletSection
        outlets={outlets}
        onChange={setOutlets}
        files={files}
        canAttach={!!saved}
        onAttach={attach}
        onRemoveFile={removeFile}
      />

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
